import React, { useEffect, useRef, useState } from "react";
import i18n from "i18next";
import {
  addColumn,
  addRow,
  parseCSV,
  removeColumn,
  removeRow,
  setCell,
  setHeader,
} from "./tableData";
/* eslint-disable react/prop-types */

/**
 * On-canvas table editor (DataTable edit mode). Every header/cell is an
 * <input>; typing edits a local draft, which is committed on input blur,
 * on every structural change (add/remove row/column, CSV import) and on exit.
 *
 * @param {object}   props.table        { keys: string[], rows: string[][] } (normalized)
 * @param {function} props.onCommit     (table) => void — persist to plugin state
 * @param {function} props.onExit       () => void — leave edit mode
 * @param {boolean}  props.autoFocus    focus the first header input on mount
 */
export default function InlineTableEditor({ table, onCommit, onExit, autoFocus }) {
  const [draft, setDraft] = useState(table);
  const committedRef = useRef(table);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const rootRef = useRef(null);
  const fileRef = useRef(null);
  const [csvError, setCsvError] = useState(false);

  const commit = (next) => {
    if (next === committedRef.current) return;
    committedRef.current = next;
    onCommit(next);
  };

  // Structural change: update draft and persist right away.
  const apply = (next) => {
    setDraft(next);
    commit(next);
  };

  // Persist pending typing when the editor goes away (exit / outside click
  // unmount us before any input blur fires).
  useEffect(() => () => commit(draftRef.current), []);

  useEffect(() => {
    if (autoFocus && rootRef.current) {
      const first = rootRef.current.querySelector("input[data-cell]");
      if (first) {
        first.focus();
        first.select();
      }
    }
  }, []);

  const focusCell = (row, col) => {
    const el = rootRef.current?.querySelector(`input[data-cell="${row}:${col}"]`);
    if (el) {
      el.focus();
      el.select();
    }
  };

  const onKeyDown = (e, row, col) => {
    // Keep editor-wide shortcuts (box delete, arrow-move, undo) out of the cell.
    e.stopPropagation();
    if (e.key === "Escape") {
      e.preventDefault();
      onExit();
    } else if (e.key === "Enter") {
      e.preventDefault();
      const next = e.shiftKey ? row - 1 : row + 1;
      if (next >= -1 && next < draftRef.current.rows.length) focusCell(next, col);
    }
  };

  const onCsvFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseCSV(String(reader.result || ""));
      if (!parsed) {
        setCsvError(true);
        return;
      }
      setCsvError(false);
      apply(parsed);
    };
    reader.readAsText(file);
  };

  const { keys, rows } = draft;
  const canRemoveCol = keys.length > 1;
  const canRemoveRow = rows.length > 1;

  return (
    <div
      ref={rootRef}
      className="dt-inline-editor pointerEventsEnabled"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      <div className="dt-inline-editor__bar">
        <button type="button" onClick={() => apply(addRow(draftRef.current))}>
          <i className="material-icons">add</i>
          {i18n.t("DataTable.inline.add_row")}
        </button>
        <button type="button" onClick={() => apply(addColumn(draftRef.current))}>
          <i className="material-icons">add</i>
          {i18n.t("DataTable.inline.add_column")}
        </button>
        <button type="button" onClick={() => fileRef.current && fileRef.current.click()}>
          <i className="material-icons">upload_file</i>
          {i18n.t("DataTable.inline.import_csv")}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          style={{ display: "none" }}
          onChange={onCsvFile}
        />
        {csvError ? (
          <span className="dt-inline-editor__error">{i18n.t("DataTable.file_msg")}</span>
        ) : null}
        <button type="button" className="dt-inline-editor__done" onClick={onExit}>
          {i18n.t("DataTable.inline.done")}
        </button>
      </div>
      <div className="dt-inline-editor__scroll">
        <table>
          <thead>
            <tr className="dt-inline-editor__col-actions">
              {keys.map((_, col) => (
                <th key={col}>
                  {canRemoveCol ? (
                    <button
                      type="button"
                      title={i18n.t("DataTable.inline.remove_column")}
                      aria-label={i18n.t("DataTable.inline.remove_column")}
                      onClick={() => apply(removeColumn(draftRef.current, col))}
                    >
                      ×
                    </button>
                  ) : null}
                </th>
              ))}
              <th />
            </tr>
            <tr>
              {keys.map((key, col) => (
                <th key={col}>
                  <input
                    data-cell={`-1:${col}`}
                    value={key}
                    placeholder={i18n.t("DataTable.inline.header_placeholder")}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, keys: setHeader(d.keys, col, e.target.value) }))
                    }
                    onBlur={() => commit(draftRef.current)}
                    onKeyDown={(e) => onKeyDown(e, -1, col)}
                  />
                </th>
              ))}
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, r) => (
              <tr key={r}>
                {row.map((cell, col) => (
                  <td key={col}>
                    <input
                      data-cell={`${r}:${col}`}
                      value={cell}
                      onChange={(e) =>
                        setDraft((d) => ({ ...d, rows: setCell(d.rows, r, col, e.target.value) }))
                      }
                      onBlur={() => commit(draftRef.current)}
                      onKeyDown={(e) => onKeyDown(e, r, col)}
                    />
                  </td>
                ))}
                <td className="dt-inline-editor__row-action">
                  {canRemoveRow ? (
                    <button
                      type="button"
                      title={i18n.t("DataTable.inline.remove_row")}
                      aria-label={i18n.t("DataTable.inline.remove_row")}
                      onClick={() => apply(removeRow(draftRef.current, r))}
                    >
                      ×
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
/* eslint-enable react/prop-types */
